import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound } from 'lucide-react';
import { changePasswordSchema, type ChangePasswordInput } from '@shared/contracts/auth';
import { useChangePassword, useLogout } from '../../features/auth/useAuth';
import { useT } from '../../i18n/useT';
import { Button } from '../ui/Button';
import { TextField } from '../ui/Field';
import { ZeeLogo } from '../ZeeLogo';

/**
 * Zorunlu şifre değiştirme ekranı.
 *
 * Yönetici tarafından açılan hesaplar tek kullanımlık bir şifre alır ve
 * `mustChangePassword` işaretiyle gelir. Sunucu bu işaret kalkana kadar
 * /auth/me ve /auth/me/password DIŞINDA hiçbir uca izin vermez
 * (`requireAuth` → 403 PASSWORD_ROTATION_REQUIRED).
 *
 * Bu ekran o kilidin kullanıcıya görünen yüzüdür. Olmasaydı, kilit bir güvenlik
 * önlemi değil bir kilitlenme olurdu: kullanıcı giriş yapabilir ama hiçbir şey
 * yapamazdı ve nedenini de öğrenemezdi.
 *
 * Ekrandan çıkışa izin verilir — şifresini bilmeyen birinin hesapta mahsur
 * kalmaması için tek çıkış yolu budur.
 */
export function PasswordRotationPage() {
  const t = useT();
  const changePassword = useChangePassword();
  const logout = useLogout();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordInput>({ resolver: zodResolver(changePasswordSchema) });

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-canvas px-4 py-12"
      data-testid="password-rotation-page"
    >
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <ZeeLogo size="lg" showTextLabel />
        </div>

        <div className="rounded-sm border border-line bg-surface p-8 shadow-sm">
          <div className="mb-6 flex items-start gap-3">
            <span
              aria-hidden
              className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-action text-action-ink"
            >
              <KeyRound className="h-4 w-4" />
            </span>
            <div>
              <h1 className="font-serif text-lg font-semibold text-ink">
                {t.passwordRotation.title}
              </h1>
              <p className="mt-1 text-xs leading-relaxed text-ink-2">
                {t.passwordRotation.description}
              </p>
            </div>
          </div>

          <form
            className="space-y-5"
            onSubmit={(event) =>
              void handleSubmit((values) => changePassword.mutate(values))(event)
            }
            noValidate
          >
            <TextField
              label={t.passwordRotation.currentPassword}
              type="password"
              autoComplete="current-password"
              hint={t.passwordRotation.currentPasswordHint}
              error={errors.currentPassword?.message}
              data-testid="rotation-current-password"
              {...register('currentPassword')}
            />
            <TextField
              label={t.passwordRotation.newPassword}
              type="password"
              autoComplete="new-password"
              hint={t.auth.passwordHint}
              error={errors.newPassword?.message}
              data-testid="rotation-new-password"
              {...register('newPassword')}
            />

            <Button
              type="submit"
              intent="accent"
              size="lg"
              block
              isLoading={changePassword.isPending}
              data-testid="rotation-submit"
            >
              {t.passwordRotation.submit}
            </Button>
          </form>

          <button
            type="button"
            onClick={() => logout.mutate()}
            data-testid="rotation-logout"
            className="mt-6 w-full text-center text-[11px] uppercase tracking-wider text-ink-3 underline-offset-4 transition-colors hover:text-ink-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {t.passwordRotation.signOut}
          </button>
        </div>
      </div>
    </div>
  );
}
